import { z } from 'zod';

import type {
  DisableMyQuickLoginPinRequest,
  GetMyQuickLoginResponse,
  GetQuickLoginPolicyResponse,
  ListQuickLoginAdminUsersResponse,
  ListQuickLoginProfilesResponse,
  QuickLoginPinLoginRequest,
  QuickLoginPinLoginResponse,
  QuickLoginPolicy,
  QuickLoginStepUpPayload,
  ResetQuickLoginPinRequest,
  SetMyQuickLoginPinRequest,
  UpdateQuickLoginEligibilityRequest,
  UpdateQuickLoginPolicyRequest,
} from './quick-login.dto.js';
import { LoginResponseSchema } from './login.schema.js';

export const QuickLoginStepUpMethodSchema = z.enum(['pin', 'password']);

export const QuickLoginStepUpPayloadSchema: z.ZodType<QuickLoginStepUpPayload> = z
  .object({
    method: QuickLoginStepUpMethodSchema,
    value: z.string().min(1),
  })
  .strict();

export const QuickLoginPolicySchema: z.ZodType<QuickLoginPolicy> = z
  .object({
    allowFourDigitPin: z.boolean(),
    sessionAutoLockMinutes: z.int().min(1).max(240),
  })
  .strict();

export const QuickLoginProfileSchema = z
  .object({
    userId: z.uuid(),
    email: z.string().trim().pipe(z.email()),
    displayName: z.string().trim().min(1),
    hasPin: z.boolean(),
    pinDigits: z.union([z.literal(4), z.literal(6)]).nullable(),
  })
  .strict();

export const ListQuickLoginProfilesResponseSchema: z.ZodType<ListQuickLoginProfilesResponse> = z
  .object({
    profiles: z.array(QuickLoginProfileSchema),
  })
  .strict();

export const QuickLoginPinLoginRequestSchema: z.ZodType<QuickLoginPinLoginRequest> = z
  .object({
    userId: z.uuid(),
    pin: z.string().min(1),
  })
  .strict();

export const QuickLoginPinLoginResponseSchema: z.ZodType<QuickLoginPinLoginResponse> = LoginResponseSchema;

export const QuickLoginAdminUserSchema = z
  .object({
    userId: z.uuid(),
    email: z.string().trim().pipe(z.email()),
    displayName: z.string().trim().min(1),
    isEligible: z.boolean(),
    hasPin: z.boolean(),
    pinDigits: z.union([z.literal(4), z.literal(6)]).nullable(),
  })
  .strict();

export const ListQuickLoginAdminUsersResponseSchema: z.ZodType<ListQuickLoginAdminUsersResponse> = z
  .object({
    users: z.array(QuickLoginAdminUserSchema),
  })
  .strict();

export const UpdateQuickLoginEligibilityRequestSchema: z.ZodType<UpdateQuickLoginEligibilityRequest> = z
  .object({
    userId: z.uuid(),
    isEligible: z.boolean(),
    stepUp: QuickLoginStepUpPayloadSchema,
  })
  .strict();

export const ResetQuickLoginPinRequestSchema: z.ZodType<ResetQuickLoginPinRequest> = z
  .object({
    userId: z.uuid(),
    stepUp: QuickLoginStepUpPayloadSchema,
  })
  .strict();

export const QuickLoginOkResponseSchema = z
  .object({
    ok: z.literal(true),
  })
  .strict();

export const GetMyQuickLoginResponseSchema: z.ZodType<GetMyQuickLoginResponse> = z
  .object({
    userId: z.uuid(),
    email: z.string().trim().pipe(z.email()),
    displayName: z.string().trim().min(1),
    isEligible: z.boolean(),
    hasPin: z.boolean(),
    pinDigits: z.union([z.literal(4), z.literal(6)]).nullable(),
  })
  .strict();

export const SetMyQuickLoginPinRequestSchema: z.ZodType<SetMyQuickLoginPinRequest> = z
  .object({
    newPin: z.string().min(1),
    currentPin: z.string().min(1).optional(),
    stepUp: QuickLoginStepUpPayloadSchema.optional(),
  })
  .strict();

export const DisableMyQuickLoginPinRequestSchema: z.ZodType<DisableMyQuickLoginPinRequest> = z
  .object({
    currentPin: z.string().min(1),
  })
  .strict();

export const GetQuickLoginPolicyResponseSchema: z.ZodType<GetQuickLoginPolicyResponse> = z
  .object({
    policy: QuickLoginPolicySchema,
  })
  .strict();

export const UpdateQuickLoginPolicyRequestSchema: z.ZodType<UpdateQuickLoginPolicyRequest> = z
  .object({
    stepUp: QuickLoginStepUpPayloadSchema,
    allowFourDigitPin: z.boolean().optional(),
    sessionAutoLockMinutes: z.int().min(1).max(240).optional(),
  })
  .strict()
  .refine(
    (value) => value.allowFourDigitPin !== undefined || value.sessionAutoLockMinutes !== undefined,
    {
      message: 'At least one policy field must be provided.',
      path: ['allowFourDigitPin'],
    },
  );
